import { useAdHandler } from '../composables/use-ad-handler'
import adaAutonative from '../static-pages/ada-autonative'
import serverError from '../static-pages/server-error'
import serverForbidden from '../static-pages/server-forbidden'
import { getAxios } from './axios'

import type { Brand } from '@xcel-next/core/enums'
import type { ComposedPage } from '@xcel-next/composer-schema'
import type { FetchPageContext, FetchPageResponse } from '../types'

const isProd = process.env.NODE_ENV === 'production'

const getNoneContentRoutes = (): Record<string, Record<string, string>> => {
  // eventuell auch eine andere datenbeschaffung möglich -> longrun
  return {}
}

export async function fetchPage (context: FetchPageContext, proxyServiceURL?: string): Promise<FetchPageResponse> {
  const { url, brand, sweepstakesActive, rsConfig, crawlerDetected } = context
  const adFreeBrand = ((process.env.AD_FREE_BRANDS as unknown as string[]) ?? []).includes(brand)
  const noCollections = !!(!isProd && url.includes('collections=false'))
  const adaNativeRegex = /^\/(autonative|pagestrip)(\/(artikel|article)(?:\.html)?)?(\?.*)?$/

  const adaNativeRequest = adaNativeRegex.test(url) ? url.match(adaNativeRegex)![1] : false
  const encodedUrlParameter = encodeURIComponent(url)
  // This is not really an env variable. It will get replaced by webpack with what's in the configs under composerServiceUrl
  const composerUrl = proxyServiceURL ?? `${process.env.COMPOSER_URL}` // 'http://localhost:3200/v2'
  let constructedUrl = `${composerUrl}/page/${brand}?url=${encodedUrlParameter}&fields=metaData.teaser:0`

  // workaround until whitelabel gets branded and provides data on gui, cs, etc
  if (brand === 'whitelabel') {
    constructedUrl = `${composerUrl}/page/maennersache?url=${encodedUrlParameter}&fields=metaData.teaser:0`
  }

  // lets fetch pageData for Home on adaAutonative request, to get navigation structure
  if (adaNativeRequest) {
    constructedUrl = `${composerUrl}/page/${brand}?url=%2F&fields=metaData.teaser:0`
  }
  const urlWithoutParams = url.split('?')[0]
  if (sweepstakesActive && (urlWithoutParams === '/gewinnspiele' || urlWithoutParams.startsWith('/gewinnspiele/'))) {
    if (urlWithoutParams.replace(/\/\s*$/, '').split('/').length > 3) {
      // Sweepstakes do not support folder structures, only 1 depth. (Ignore trailing slash with the replace while checking)
      // else will result in 503, so we just fetch the 404 page from composer to save the composer throwing a 503
      constructedUrl = `${composerUrl}/page/${brand}?url=%2F404`
    } else {
      const sweepstakesUrl = new URL(composerUrl + url)
      sweepstakesUrl.pathname = sweepstakesUrl.pathname.replace('/gewinnspiele', '/sweepstakes')
      sweepstakesUrl.search = new URLSearchParams({
        brand,
        ...(sweepstakesUrl.searchParams.has('page') && { page: sweepstakesUrl.searchParams.get('page')! })
      }).toString()
      constructedUrl = sweepstakesUrl.href
    }
  }

  // if url has a brand parameter we pass this to the frontend only (not the composer) - this helps with checking content with styles of other brands
  const frontendBrand = (isProd ? brand : (url.split('brand=').splice(1).join('').split('&')[0] || brand)) as Brand

  const noneContentRoutes = getNoneContentRoutes()
  if (noneContentRoutes[brand] && noneContentRoutes[brand][url]) {
    return {
      pageData: {
        header: {},
        body: {
          headline: '',
          paragraphs: [
            {
              paragraphType: 'vueComponent',
              vueComponent: {
                componentName: noneContentRoutes[brand][url]
              }
            }
          ]
        },
        pageMeta: {},
        metaData: {}
      },
      brand: frontendBrand,
      imageBrand: brand,
      status: 200
    }
  }

  try {
    const axios = await getAxios()
    const config = context.token
      ? {
        headers: {
          authorization: context.token
        }
      }
      : undefined
    const requestUrl = new URL(constructedUrl)
    requestUrl.searchParams.set('crawlerDetected', crawlerDetected ? 'true' : 'false')
    constructedUrl = requestUrl.href
    const asset = await axios.get(constructedUrl, config)

    // catch AdA native page request (autonative and pagestrip) and enrich static data
    if (adaNativeRequest) {
      const adaNativeData = (adaNativeRequest === 'autonative'
        ? url.includes('.html') || url.includes('/article') || url.includes('/artikel') ? adaAutonative.article : adaAutonative.structureterm
        : adaAutonative.pagestrip) as unknown as ComposedPage
      adaNativeData.header.menu = asset.data.header.menu
      adaNativeData.footer = asset.data.footer
      adaNativeData.pageMeta.url = url
      asset.data = adaNativeData
    }

    const { embedAds } = useAdHandler()
    const pageData = adFreeBrand ? asset.data : noCollections ? asset.data : embedAds(asset.data, rsConfig)

    return {
      pageData,
      brand: frontendBrand,
      imageBrand: brand,
      status: 200
    }
  } catch (err: any) {
    if (err.response?.status === 403) {
      return {
        status: 403,
        pageData: serverForbidden,
        brand
      }
    } else if (err.response && err.response.status !== 404 && !(err.response.data &&
      err.response.data.customInfo &&
      err.response.data.customInfo.redirectCode === 301 &&
      err.response.data.customInfo.redirectUrl)) {
      console.error(`ERROR FROM COMPOSER FETCHING URL: ${constructedUrl} - `, err)
    }
    if (err.response &&
      err.response.data &&
      err.response.data.customInfo &&
      err.response.data.customInfo.redirectCode === 301 &&
      err.response.data.customInfo.redirectUrl) {
      const redirectError: any = new Error('Redirect required')
      const status = err.response.data.customInfo.redirectCode

      // append all parameter to redirect url excluding page=0 and page=1
      const urlParams = new URLSearchParams(url.indexOf('?') >= 0 ? url.slice(url.indexOf('?')) : '')
      if (['0', '1'].includes(urlParams.get('page') ?? '')) {
        urlParams.delete('page')
      }
      const query = urlParams.toString() !== '' ? `?${urlParams.toString()}` : ''

      const redirectUrl = `${err.response.data.customInfo.redirectUrl}${query}`
      redirectError.data = { status, redirectUrl }
      // we throw an error because nothing has to be rendered
      throw redirectError
    } else if (err.response &&
      err.response.data &&
      err.response.data.customInfo &&
      err.response.data.status &&
      err.response.data.status === 404) {
      // we return an object because the 404 page has to be rendered
      return {
        status: 404,
        pageData: err.response.data.customInfo,
        brand
      }
    } else {
      console.error('ERROR : 503 in api/index.ts', err)
      // we return an object because the 503 page has to be rendered
      return {
        status: 503,
        pageData: serverError,
        brand
      }
    }
  }
}
